import type {
  Classification,
  CopyReferenceCommand,
  CreateSectorCommand,
  DraftValidation,
  LeadSectorBrief,
  LeadSectorSummary,
  PositionQuality,
  PublicationContent,
  PublicationPreview,
  PublishCommand,
  PublishResult,
  RestorePublicationCommand,
  SectorHistory,
  RouteSectors,
  SaveAvailabilityCommand,
  SaveSectorAreaCommand,
  SectorSource,
  SectorWorkspace,
  ServiceLocationSector,
  SetLeadSectorCommand,
  SetLocationSectorOverrideCommand,
  SectorWriteResult,
  SetSectorArchivedCommand,
  UpdateSectorCommand,
} from '../domain/sector'

/** Référentiel des secteurs : lecture de l'espace de travail et écritures du brouillon (jamais l'état publié). */
export interface SectorRepository {
  getWorkspace(): Promise<SectorWorkspace>
  createSector(command: CreateSectorCommand): Promise<SectorWriteResult>
  updateSector(command: UpdateSectorCommand): Promise<SectorWriteResult>
  setSectorArchived(command: SetSectorArchivedCommand): Promise<SectorWriteResult>
  saveArea(command: SaveSectorAreaCommand): Promise<SectorWriteResult>
  saveAvailability(command: SaveAvailabilityCommand): Promise<SectorWriteResult>
  /** Problèmes de chaque contour et chevauchements, calculés par PostGIS. */
  getDraftValidation(): Promise<DraftValidation>
  /** Copie une limite de référence vers le brouillon d'un secteur (provenance `REFERENCE_COPY`). */
  copyReference(command: CopyReferenceCommand): Promise<SectorWriteResult>
  /** Vérifie une position (règle des 10 m) sur le brouillon ou la version publiée. */
  classifyPoint(
    longitude: number,
    latitude: number,
    quality: PositionQuality,
    source: SectorSource,
  ): Promise<Classification>
  /** Adresses de service et leur secteur (exige aussi `contract.read`). */
  getServiceLocations(source: SectorSource, season?: string): Promise<ServiceLocationSector[]>
  /** Secteurs traversés par chaque route, en lecture seule (exige aussi `route.read`). */
  getRouteSectors(source: SectorSource, season?: string): Promise<RouteSectors[]>
  /** Corrige le secteur d'une adresse; la valeur automatique reste conservée. */
  setLocationOverride(command: SetLocationSectorOverrideCommand): Promise<void>
  /** Comparatif d'impact calculé par le serveur avant publication. */
  getPublicationPreview(): Promise<PublicationPreview>
  /** Publie le brouillon en une nouvelle version immuable, en une seule transaction. */
  publish(command: PublishCommand): Promise<PublishResult>
  /** Historique : brouillon, versions publiées, brouillons archivés. */
  getHistory(): Promise<SectorHistory>
  /** Contenu d'une version publiée, en lecture seule. */
  getPublication(publicationId: string): Promise<PublicationContent>
  /** Restaure une version en nouveau brouillon; le brouillon courant est archivé d'abord. */
  restorePublication(command: RestorePublicationCommand): Promise<SectorWriteResult>
  /** Secteur d'une demande : état courant et historique (exige aussi `lead.read`). */
  getLeadSector(leadId: string): Promise<LeadSectorSummary>
  /** Secteur retenu de plusieurs demandes, sans historique (exige `lead.read`). */
  getLeadSectors(leadIds: string[]): Promise<LeadSectorBrief[]>
  /** Nouvelle évaluation sur la dernière version publiée; une correction manuelle reste en place. */
  recheckLeadSector(leadId: string): Promise<void>
  /** Corrige le secteur d'une demande, ou revient à la valeur automatique. */
  setLeadSector(command: SetLeadSectorCommand): Promise<void>
}

export class SectorError extends Error {
  constructor(readonly code: string) {
    super(code)
    this.name = 'SectorError'
  }
}
